import React from "react";
import {Form, Row, Col, Input, Select, Button} from "antd";
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {IQualification} from "../types/qualification.types";
import DocumentUploader from "../../../../common/document-uploader/document-uploader";
import {Document} from "../../../../../domains/models/document/index";

interface IHigherEducationProps {
    form: any;
    required: boolean;
    docType: string;
    fieldName: string;
    qualifications: IQualification[];
    employeeId: string;
    onUploadSuccess:(doc:Document)=>void;
}

const HigherEducation = (props: IHigherEducationProps) => {

    function setDegreeCode(name: number, value: string) {
        const selected = props.qualifications.find((q: IQualification) => q.id === value);
        props.form.setFieldValue([props.fieldName, name, "degreeCode"], selected?.code);
    }

    return (
        <section className={'pn__employee-create__form__fixed-width higher-education'}>
            <Form.List name={props.fieldName}>
                {(fields, {add, remove}) => {
                    return (
                        <>
                            {fields.map(({key, name, ...restField}) => (
                                <div key={key} className={'graduationBox'}>

                                    {/* Passing Year and GPA/CGPA */}
                                    <Row gutter={10}>
                                        <Col span={12}>
                                            <Form.Item
                                                {...restField}
                                                label={"Passing Year"}
                                                name={[name, "passingYear"]}
                                                className={"formInputFields"}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: "Please enter Passing Year",
                                                    },
                                                    {
                                                        pattern: /^(?:\d{4})(?:-\d{4})?$/,
                                                        message: "Please enter a valid year or year range (e.g., 2020 or 2020-2025)",
                                                    },
                                                    {
                                                        validator: (_, value) => {
                                                            if (value) {
                                                                const [startYear, endYear] = value.split('-').map(Number);
                                                                if (startYear > new Date().getFullYear()) {
                                                                    return Promise.reject("Start year cannot be in the future.");
                                                                }
                                                                if (endYear && endYear < startYear) {
                                                                    return Promise.reject("End year must be greater than or equal to start year.");
                                                                }
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}>
                                                <Input placeholder={'Enter passing year'}/>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "cgpa"]}
                                                label={"CGPA"}
                                                className={"formInputFields"}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: "Please enter GPA / CGPA",
                                                    },
                                                    {
                                                        validator: (_, value) => {
                                                            if (value === undefined || value === '') {
                                                                return Promise.reject("This field is required");
                                                            }
                                                            const num = parseFloat(value);
                                                            if (isNaN(num)) {
                                                                return Promise.reject("Please enter a valid number.");
                                                            }
                                                            const regex = /^\d+(\.\d{1,2})?$/;
                                                            if (!regex.test(value)) {
                                                                return Promise.reject("Please enter a valid value with up to two decimal places.");
                                                            }
                                                            if (num >= 0 && num <= 10) {
                                                                return Promise.resolve();
                                                            }
                                                            if (num > 10 && num <= 100) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject("Enter either CGPA (0–10) or Percentage (0–100).");
                                                        }
                                                    }
                                                ]}
                                            >
                                                <Input placeholder={'Enter percentage / CGPA'}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* Degree */}
                                    <Row gutter={10}>
                                        <Col span={12}>
                                            <Form.Item
                                                {...restField}
                                                label={"Degree"}
                                                name={[name, "degreeId"]}
                                                className={"formInputFields"}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: "Please select Degree",
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    placeholder="Select degree"
                                                    onChange={(value) => setDegreeCode(name, value)}
                                                >
                                                    {props.qualifications.map((qualification: IQualification) => (
                                                        <Select.Option
                                                            key={qualification.id}
                                                            value={qualification.id}
                                                        >
                                                            {qualification.displayName}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "degreeCode"]}
                                                className={"formInputFields"}
                                                hidden={true}
                                            >
                                                <Input/>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* Board or University */}
                                    <Row gutter={10}>
                                        <Col span={12}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "boardOrUniversity"]}
                                                label={"University/Institute"}
                                                className={"formInputFields"}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: "Please enter University/Institute",
                                                    },
                                                ]}
                                            >
                                                <Input placeholder={'Enter university/institute'}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* Graduation Document Upload */}
                                    <Row gutter={10}>
                                        <Col span={12}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "documentId"]}
                                                label={"Upload Document"}
                                                style={{marginBottom: 'unset'}}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: `Please upload the ${props.docType} Document`,
                                                    },
                                                ]}
                                            >
                                                <DocumentUploader
                                                    disabled={false}
                                                    docType={props.docType}
                                                    purpose={'EMPLOYMENT'}
                                                    employeeId={props.employeeId}
                                                    onUploadSuccess={props.onUploadSuccess}
                                            />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <div className={"removeAndResetInGraduation"}>
                                        <Button danger onClick={() => remove(name)} className="remove-button">
                                            <DeleteOutlined/> Remove
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            <div className={"employee-create__actions add-btn"}>
                                <Button
                                    type="primary"
                                    shape={'round'}
                                    icon={<PlusOutlined/>}
                                    onClick={() => add()}
                                >
                                    Add New Field
                                </Button>
                            </div>
                        </>
                    )
                }}
            </Form.List>
        </section>
    )
}

export default HigherEducation;
